import { z } from 'zod';
import { isValidIBAN, isValidBIC } from 'ibantools';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { AccountType } from '@/features/dashboard/paymentConfig/types/AccountType.ts';

const bankAccountBaseSchema = z.object({
  accountType: z.enum([AccountType.NRB, AccountType.IBAN], {
    error: 'Typ konta jest wymagany',
  }),
  bankName: z.string().min(1, 'Nazwa banku jest wymagana'),
  accountNumber: z.string().min(1, 'Numer konta jest wymagany'),
  companyAddress: z.string().optional(),
  swiftCode: z.string().optional(),
});

const bankAccountSuperRefine = (
  data: z.infer<typeof bankAccountBaseSchema>,
  ctx: z.RefinementCtx,
) => {
  const stripped = data.accountNumber.replace(/\s/g, '');

  if (data.accountType === AccountType.NRB) {
    if (!isValidIBAN('PL' + stripped)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Nieprawidłowy numer konta',
        path: ['accountNumber'],
      });
    }
  } else {
    if (!isValidIBAN(stripped)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Nieprawidłowy format numeru IBAN',
        path: ['accountNumber'],
      });
    }
    if (!data.swiftCode || data.swiftCode.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Kod SWIFT jest wymagany dla konta IBAN',
        path: ['swiftCode'],
      });
    } else if (!isValidBIC(data.swiftCode.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Nieprawidłowy kod SWIFT',
        path: ['swiftCode'],
      });
    }
  }
};

export const createBankAccountSchema = z
  .object({
    currency: z.enum([Currency.PLN, Currency.EUR, Currency.USD], {
      error: 'Waluta jest wymagana',
    }),
  })
  .merge(bankAccountBaseSchema)
  .superRefine(bankAccountSuperRefine);

export type CreateBankAccountFormData = z.infer<typeof createBankAccountSchema>;
export type CreateBankAccountInputData = z.input<
  typeof createBankAccountSchema
>;

export const createBankAccountDefaultValues: CreateBankAccountInputData = {
  currency: Currency.PLN,
  accountType: AccountType.NRB,
  bankName: '',
  accountNumber: '',
  companyAddress: '',
  swiftCode: '',
};

export const editBankAccountSchema = bankAccountBaseSchema.superRefine(
  bankAccountSuperRefine,
);

export type EditBankAccountFormData = z.infer<typeof editBankAccountSchema>;
export type EditBankAccountInputData = z.input<typeof editBankAccountSchema>;
